/**
 * Newcomers at the gate (v1 ticket 42), in home's bottom panel above Record and Type: while `GET /api/town` carries
 * an offer, one line ("Someone new is at the gate: *saying hello*, near *At the counter*.") with **Meet them**,
 * **Someone else** and **Not now**; on a day whose newcomers could not be written, one quiet line instead.
 *
 * - **Someone else** swaps the offer in place (the server's next situation; none left today hides it).
 * - **Not now** hides it until the learner's next day, on every device (the server keeps it).
 * - **Meet them** waits ("Newcomers are on their way to *[town]*…"), then goes to the moment's landing (`/land/<id>`),
 *   as a new moment does. Any other refusal is one line, and a gone or failed offer goes with it.
 * - **The beta's warm-up:** while today's review batch is open (home's allowance says so, or Meet them was refused
 *   with 409 `daily-review`, which fixes the batch), the newcomers wait: the requirement card (the gate line and
 *   **Review now**, the capture form's wording) stands where Meet them was, with Someone else and Not now kept; a
 *   refusal raises `newcomers-gated` so home re-reads its allowance and round (the batch is a card the round now
 *   serves, so "Today's round is done" would be stale). Before the batch is fixed, the quiet preview line under the
 *   actions says the first new moment starts with a review.
 *
 * - **The look:** a small gate scene beside the words (`gateScene`, `web/src/town/figure.ts`): the town's gate arch, a figure with a lantern
 *   under it, the lantern breathing; while Meet them waits the figure walks and the lantern swings; while the review
 *   holds them the figure sits still with the lantern low. Motion is off under `prefers-reduced-motion`. The words
 *   carry a "newcomers" pill and the situation and place in gold.
 *
 * - **The revamp (one person who stays):** the offer names who comes ("**Jordi** is at the gate: …"); Meet them's wait
 *   is the arrival stage (`gateScene('arriving')`), "Jordi is coming through the gate…". A `waiting` offer (a newcomer
 *   from an earlier day whose question has no answer, 1 to 3 days) stands instead: the figure with its lantern, "Jordi
 *   is still waiting for your answer.", the question, **Answer Jordi** (the Type panel answering that moment, held by
 *   the day's review as Meet them is) and **Not now**. An older server's offer without `kind` or name reads as before.
 *
 * The offer is held here, not in home's town: home hands the town's offer in, and a new town read starts over from it.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, HttpError } from '../api';
import { errorText, t } from '../i18n';
import { tPart, tParts } from '../bidi-html';
import { go } from '../router';
import { gateRefusal, renderGate } from '../review-gate';
import type { NewcomerReadiness, NewcomersOffer, NewcomersWaiting } from '../town/own-town';
import { figure, gateScene } from '../town/figure';
import { openAnswer } from './capture-open';
import { answerLabel, nameHtml } from './newcomer-name';
import type { ReviewGate } from '../types';

/** Meet them shows the arrival stage at least this long, so a scene that comes back at once still walks in. */
const ARRIVAL_HOLD_MS = 1400;

@customElement('newcomers-offer')
export class NewcomersOfferView extends LitElement {
  /** The offer as the town was read (null: none). */
  @property({ attribute: false }) offer: NewcomersOffer | null = null;
  /** Server-owned reason and eligible action while no offer stands. */
  @property({ attribute: false }) readiness?: NewcomerReadiness;
  /** The town says today's newcomers could not be written. */
  @property({ type: Boolean }) failed = false;
  /** The town's name, for the wait (unnamed: "your town"). */
  @property({ attribute: false }) townName?: string;
  /** Today's review requirement as home's allowance reads it (beta); none in personal mode. */
  @property({ attribute: false }) gate?: ReviewGate;
  /** The authoritative daily policy timezone from the allowance response. */
  @property({ attribute: false }) timezone?: string;
  /** What shows: the town's offer until Someone else, Not now or a refusal changes it. */
  @state() private shown: NewcomersOffer | null = null;
  @state() private failedNow = false;
  @state() private busy: '' | 'meet' | 'skip' | 'notNow' = '';
  @state() private error = '';
  /** A server refresh owns the home snapshot; clearing its local offer must not emit a second, partial update. */
  private refreshedTown = false;
  /** The requirement as Meet them's refusal reported it, until the town is read again. */
  @state() private refusedGate?: ReviewGate;

  protected createRenderRoot() { return this; }

  protected willUpdate(changed: Map<string, unknown>) {
    if (changed.has('offer') || changed.has('failed')) {
      this.shown = this.offer;
      this.failedNow = this.failed;
      this.error = '';
      this.refusedGate = undefined;
    }
  }

  protected updated(changed: Map<string, unknown>) {
    // The offer went (Not now, Someone else with nobody left, gone, failed): home stops leaving its gate note to it.
    if (changed.has('shown')) {
      const refreshed = this.refreshedTown;
      this.refreshedTown = false;
      if (!changed.get('shown') || this.shown || refreshed) return;
      this.dispatchEvent(new CustomEvent('newcomers-hidden', { bubbles: true, detail: { failed: this.failedNow } }));
    }
  }

  /** The requirement that holds Meet them: the refusal's, else home's. */
  private get gateNow(): ReviewGate | undefined {
    return this.refusedGate ?? this.gate;
  }

  /** Re-read the server's offer and blocker after this device discovers that its town was stale. */
  private async refreshTown() {
    try {
      const town = await api.town();
      if (!this.isConnected) return;
      this.refreshedTown = true;
      this.shown = town.newcomers ?? null;
      this.readiness = town.newcomerReadiness;
      this.dispatchEvent(new CustomEvent('newcomers-town-read', { bubbles: true, detail: town }));
    } catch {
      // Keep the current explanation; a later home read will refresh it.
    }
  }

  private skip = async () => {
    const o = this.shown;
    if (!o || o.kind === 'waiting' || this.busy) return;
    this.busy = 'skip';
    this.error = '';
    try {
      this.shown = (await api.newcomersSkip(o.id)).newcomers;
    } catch (e) {
      if (e instanceof HttpError && e.code === 'newcomers-gone') void this.refreshTown();
      else this.error = errorText(e);
    } finally {
      this.busy = '';
    }
  };

  private notNow = async () => {
    if (this.busy) return;
    this.busy = 'notNow';
    this.error = '';
    try {
      await api.newcomersNotNow();
      await this.refreshTown();
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.busy = '';
    }
  };

  private meet = async () => {
    const o = this.shown;
    if (!o || o.kind === 'waiting' || this.busy) return;
    this.busy = 'meet';
    this.error = '';
    this.refusedGate = undefined;
    try {
      // The arrival stage stays up for at least a moment even when the scene comes back at once (mock mode, a cached
      // answer), so the walk from the gate is seen rather than flashed; reduced motion skips the hold.
      const hold = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : ARRIVAL_HOLD_MS;
      const [m] = await Promise.all([api.meetNewcomers(o.id), new Promise((r) => window.setTimeout(r, hold))]);
      if (this.isConnected) go(`/land/${m.id}`);
      return;
    } catch (e) {
      const refused = gateRefusal(e);
      if (refused) {
        this.refusedGate = { status: 'pending', ...refused, resetsAt: '' };
        this.dispatchEvent(new CustomEvent('newcomers-gated', { bubbles: true }));
      } else {
        const code = e instanceof HttpError ? e.code : undefined;
        if (code === 'newcomers-gone') {
          await this.refreshTown();
        }
        if (code === 'newcomers-failed') {
          this.shown = null;
          this.failedNow = true;
        } else if (code !== 'newcomers-gone') this.error = errorText(e);
      }
    }
    this.busy = '';
  };

  render() {
    const o = this.shown;
    if (this.busy === 'meet') return this.renderArriving(o);
    if (!o) {
      if (this.failedNow || this.error) return html`<div class="newcomers newcomers-quiet muted small" role="status">${this.error || t('newcomers.failed')}</div>`;
      const r = this.readiness;
      if (!r) return nothing;
      const action = 'action' in r ? r.action : undefined;
      const href = r.reason === 'landing' ? `/land/${r.momentId}` : action === 'capture' ? '/record#wanted' : action === 'round' ? '/round' : undefined;
      return html`<div class="newcomers newcomers-readiness muted small" role="status">
        <span>${t(`newcomers.readiness.${r.reason}`)}</span>
        ${action && href ? html`<a class="btn ghost small" href=${href} @click=${(e: Event) => { e.preventDefault(); go(href); }}>${t(`newcomers.readiness.action.${action}`)}</a>` : nothing}
      </div>`;
    }
    const gate = this.gateNow;
    const gated = gate?.status === 'pending';
    if (o.kind === 'waiting') return this.renderWaiting(o, gate, gated);
    return html`<div class="newcomers" data-kind="arrive" data-situation=${o.id} ?data-gated=${gated}>
      <div class="nc-head">
        <span class="nc-art">${gateScene(gated ? 'still' : 'at-gate')}</span>
        <div class="nc-words">
          <span class="pill newcomers-pill nc-pill">${t('newcomers.pill')}</span>
          <div class="newcomers-line">${tParts(o.name ? 'newcomers.offerNamed' : 'newcomers.offer', {
            ...(o.name ? { name: html`<strong>${nameHtml({ name: o.name, script: o.script }, true)}</strong>` } : {}),
            title: html`<em><bdi dir="auto">${o.title}</bdi></em>`,
            place: html`<em><bdi dir="auto">${o.near.title}</bdi></em>`,
          })}</div>
        </div>
      </div>
      ${gated ? html`<div class="newcomers-gated muted small">${t('newcomers.gated')}</div>${renderGate(gate, { timezone: this.timezone })}` : nothing}
      <div class="newcomers-actions">
        ${gated ? nothing : html`<button class="btn primary newcomers-meet" ?disabled=${!!this.busy} @click=${this.meet}>${t('newcomers.meet')}</button>`}
        <button class="btn newcomers-else" ?disabled=${!!this.busy} @click=${this.skip}>${t('newcomers.else')}</button>
        <button class="btn ghost newcomers-not-now" ?disabled=${!!this.busy} @click=${this.notNow}>${t('newcomers.notNow')}</button>
      </div>
      ${this.error ? html`<div class="newcomers-error muted small" role="status">${this.error}</div>` : nothing}
      ${gated ? nothing : renderGate(gate, { timezone: this.timezone })}
    </div>`;
  }

  /**
   * Meet them's wait: the arrival stage (the figure walking from the gate to an empty plot, again and again) and
   * "Jordi is coming through the gate…"; an older server's offer without a name keeps "Newcomers are on their way".
   */
  private renderArriving(o: NewcomersOffer | null) {
    const name = o && o.kind !== 'waiting' ? o.name : undefined;
    const town = this.townName;
    const line = name
      ? tPart('newcomers.arriving', 'name', html`<strong>${nameHtml({ name, script: o?.script }, true)}</strong>`)
      : town ? tParts('newcomers.onTheWay', { town: html`<bdi dir="auto">${town}</bdi>` }) : t('newcomers.onTheWayHome');
    return html`<div class="newcomers newcomers-wait nc-arriving" role="status">
      <span class="nc-stage">${gateScene('arriving')}</span>
      <span class="nc-wait-line">${line}</span>
    </div>`;
  }

  /**
   * A newcomer from an earlier day still waiting for the learner's answer: the figure with its lantern, "Jordi is still
   * waiting for your answer.", the question (Finglish first, the script after, the meaning under), **Answer Jordi**
   * (the Type panel answering that moment; held by the day's review exactly as Meet them) and **Not now**.
   */
  private renderWaiting(o: NewcomersWaiting, gate: ReviewGate | undefined, gated: boolean) {
    const who = { name: o.name, script: o.script, role: o.role };
    return html`<div class="newcomers nc-waiting" data-kind="waiting" data-moment=${o.momentId} ?data-gated=${gated}>
      <div class="nc-head">
        <span class="nc-art nc-art-person">${figure('waiting', 'offer')}</span>
        <div class="nc-words">
          <span class="pill newcomers-pill nc-pill">${t('newcomers.pill')}</span>
          <div class="newcomers-line">${tPart('newcomers.waiting', 'name', html`<strong>${nameHtml(who, true)}</strong>`)}</div>
          ${o.role ? html`<div class="muted small nc-role" dir="auto">${o.role}</div>` : nothing}
        </div>
      </div>
      <div class="answer-ask nc-question">
        ${o.questionLatin
          ? html`<bdi class="ca answer-q" dir="auto">${o.questionLatin}</bdi><bdi class="muted small answer-script" dir="auto">${o.question}</bdi>`
          : html`<bdi class="ca answer-q" dir="auto">${o.question}</bdi>`}
        ${o.translation ? html`<div class="answer-tr muted small" dir="auto">${o.translation}</div>` : nothing}
      </div>
      ${gated ? html`<div class="newcomers-gated muted small">${t('newcomers.gated')}</div>${renderGate(gate, { timezone: this.timezone })}` : nothing}
      <div class="newcomers-actions">
        ${gated ? nothing : html`<button class="btn primary newcomers-answer" ?disabled=${!!this.busy} @click=${() => openAnswer(o.momentId)}>${answerLabel(who)}</button>`}
        <button class="btn ghost newcomers-not-now" ?disabled=${!!this.busy} @click=${this.notNow}>${t('newcomers.notNow')}</button>
      </div>
      ${this.error ? html`<div class="newcomers-error muted small" role="status">${this.error}</div>` : nothing}
      ${gated ? nothing : renderGate(gate, { timezone: this.timezone })}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'newcomers-offer': NewcomersOfferView }
}
